import { normalize } from "normalizr";
import React from "react";
import { getFormValues, initialize, reset } from "redux-form";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import { analyticsStoreEvent } from "@/mk2/actions";
import {
    ARTICLE_ADD_EXPERIENCE_LOAD_PHOTOS,
    ARTICLE_ADD_EXPERIENCE_PHOTO_ENTITIES_LOADED,
    ARTICLE_ADD_EXPERIENCE_SAVE_DRAFT,
    ARTICLE_ADD_EXPERIENCE_SUBMIT,
    ARTICLE_ADD_EXPERIENCE_SUBMIT_UNAUTHENTICATED,
    ArticleAddExperienceLoadPhotosAction,
    ArticleAddExperienceSaveDraftAction,
    ArticleAddExperienceSubmitAction,
    ArticleAddExperienceSubmitUnauthenticatedAction,
    articleAddExperienceApi,
} from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperience.actions";
import { ARTICLE_ADD_EXPERIENCE_FORM_NAME } from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperience.constants";
import { XHRAction, XHRFormError } from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { photoEntitiesToSuccessfulPhotoUploads } from "@/mk2/helpers/photos";
import { getLogger } from "@/mk2/logger";
import { checkIfUserIsLoggedIn } from "@/mk2/sagas/auth";
import { PhotoSchema } from "@/mk2/schemas";
import { HistoryAction, pushInPWA } from "@/mk2/services/browserHistory";
import { WIKI_EXPERIENCE_SENT_TOAST } from "@/mk/autogenerated/translations/ArticleAddExperience.sagas.2bfdf87f1633c45dbfcb68280020438f";
import {
    bazaar_api_v3_product_experience_photos_url,
    pending_action_create_url,
    wiki_api_add_experience_url,
    wiki_api_add_experience_with_undef_article_url,
    wiki_api_create_experience_draft_url,
} from "@/mk/urls/functions";

const logger = getLogger("wiki/ArticleAddExperience.sagas");

export function* submitExperienceSaga({
    categorySlug,
    articleSlug,
    values,
    gotoUrl,
    source,
    fromProductId,
    xhr,
    location,
    routeNavigate,
}: ArticleAddExperienceSubmitAction & XHRAction & HistoryAction) {
    yield put(articleAddExperienceApi.request(articleSlug, categorySlug));
    try {
        // verifiedByAdmin is an ExperienceDraft-only concept — never let it reach a
        // classic experience (the shared form defaults the admin checkbox to true).
        const { verifiedByAdmin: _unusedVerifiedByAdmin, ...restValues } = values;
        // Carry the funnel attribution to the backend wiki_experience_created event
        // (the experience form ignores these extra keys).
        const payloadValues = {
            ...restValues,
            ...(source ? { source } : {}),
            ...(fromProductId ? { fromProduct: fromProductId } : {}),
        };
        const response =
            categorySlug && articleSlug
                ? yield call(() =>
                      xhr.post(wiki_api_add_experience_url(articleSlug), {
                          values: payloadValues,
                      }),
                  )
                : yield call(() =>
                      xhr.post(wiki_api_add_experience_with_undef_article_url(), {
                          values: payloadValues,
                      }),
                  );
        const normalizedReponse = normalize(response.body, {});
        const experienceId: number | null = response.body?.id ?? null;
        const navigateTo =
            categorySlug && articleSlug && experienceId
                ? `${gotoUrl}#experience-${experienceId}`
                : gotoUrl;
        // Funnel COMPLETE: the authenticated submit actually created the review.
        yield put(
            analyticsStoreEvent("wiki_experience_submit_success", {
                ...(source ? { source } : {}),
                ...(fromProductId ? { product_id: fromProductId } : {}),
            }),
        );
        yield handleXHRFormSuccessSaga(
            ARTICLE_ADD_EXPERIENCE_FORM_NAME,
            routeNavigate,
            navigateTo,
            WIKI_EXPERIENCE_SENT_TOAST,
        );
        yield put(articleAddExperienceApi.success(articleSlug, categorySlug, normalizedReponse));
    } catch (error) {
        yield handleXHRFormErrorSaga(
            ARTICLE_ADD_EXPERIENCE_FORM_NAME,
            error as XHRFormError,
            logger,
        );
        yield put(articleAddExperienceApi.failure(articleSlug, categorySlug, error));
    }
}

function* submitExperienceUnauthenticatedSaga({
    articleSlug,
    categorySlug,
    values,
    source,
    fromProductId,
    xhr,
    location,
    routeNavigate,
}: ArticleAddExperienceSubmitUnauthenticatedAction & XHRAction & HistoryAction) {
    try {
        const response = yield call(() =>
            xhr.post(pending_action_create_url(), {
                actionType: "wiki_experience",
                payload: {
                    articleSlug: articleSlug || null,
                    categorySlug: categorySlug || null,
                    title: values.title,
                    bodyText: values.text,
                    rating: values.rating || null,
                    positives: values.positives || null,
                    negatives: values.negatives || null,
                    articleProposal: values.articleProposal || null,
                    verifiedByExpPhotos: values.verifiedByExpPhotos || false,
                    // Guest fallback display name (auto-publish under wiki_guest
                    // if the user never registers)
                    nickname: values.authorNickname || null,
                    // Guest contact email (not published; used by admins to reach
                    // the author via the support ticket system)
                    email: values.email || null,
                    // Funnel attribution, forwarded to wiki_experience_created when
                    // the guest review is eventually published.
                    source: source || null,
                    fromProduct: fromProductId || null,
                },
            }),
        );
        const token = response.body.token;
        yield put(reset(ARTICLE_ADD_EXPERIENCE_FORM_NAME));
        const publishUrl = `/pending-action/${token}/publish/`;
        yield call(
            checkIfUserIsLoggedIn,
            routeNavigate,
            location,
            publishUrl,
            "pa-wiki-experience",
        );
    } catch (error) {
        yield handleXHRFormErrorSaga(
            ARTICLE_ADD_EXPERIENCE_FORM_NAME,
            error as XHRFormError,
            logger,
        );
    }
}

export function* saveDraftSaga({
    values,
    articleSlug,
    categorySlug,
    xhr,
    routeNavigate,
}: ArticleAddExperienceSaveDraftAction & XHRAction & HistoryAction) {
    try {
        const response: { body: { uuid: string; shortCode: string; redirectPath: string } } =
            yield call(() =>
                xhr.post(wiki_api_create_experience_draft_url(), {
                    articleSlug: articleSlug || null,
                    categorySlug: categorySlug || null,
                    title: values.title || "",
                    bodyText: values.text || "",
                    rating: values.rating || null,
                    positives: values.positives || "",
                    negatives: values.negatives || "",
                    articleProposal: values.articleProposal || "",
                    prefilledUsername: values.prefilledUsername || null,
                    prefilledPhone: values.prefilledPhone || null,
                    prefilledEmail: values.prefilledEmail || null,
                    authorNickname: values.authorNickname || null,
                    verifiedByAdmin: values.verifiedByAdmin ?? true,
                    photos: values.photos || [],
                }),
            );
        pushInPWA(routeNavigate, {
            pathname: response.body.redirectPath,
            state: { formIgnorePrompt: true },
        });
    } catch (error) {
        yield handleXHRFormErrorSaga(
            ARTICLE_ADD_EXPERIENCE_FORM_NAME,
            error as XHRFormError,
            logger,
        );
    }
}

export function* loadPhotosSaga({
    productId,
    xhr,
}: ArticleAddExperienceLoadPhotosAction & XHRAction) {
    try {
        const response = yield call(() =>
            xhr.get(bazaar_api_v3_product_experience_photos_url(productId)),
        );
        // Merge the photo entities into the store so PhotoPreview can resolve
        // them by photoId (it reads the photo from the entity cache, not the
        // form value).
        const normalized = normalize(response.body.photos, [PhotoSchema]);
        yield put({ type: ARTICLE_ADD_EXPERIENCE_PHOTO_ENTITIES_LOADED, response: normalized });

        const photos = photoEntitiesToSuccessfulPhotoUploads(response.body.photos);
        // Re-initialize (merging current values) so the prefilled photos stay
        // pristine — no false "unsaved changes". The ClickableField photos
        // preview reacts to the form value, so they show immediately.
        const currentValues = yield select(getFormValues(ARTICLE_ADD_EXPERIENCE_FORM_NAME));
        yield put(
            initialize(ARTICLE_ADD_EXPERIENCE_FORM_NAME, {
                ...(currentValues || {}),
                photos,
            }),
        );
    } catch (error) {
        // Non-owner (403), missing product, or network error: leave the
        // uploader empty rather than surfacing an error on an optional prefill.
        logger.warn("Failed to prefill experience photos from listing", error);
    }
}

export default function* root() {
    yield all([
        takeLatest(ARTICLE_ADD_EXPERIENCE_SUBMIT, submitExperienceSaga),
        takeLatest(
            ARTICLE_ADD_EXPERIENCE_SUBMIT_UNAUTHENTICATED,
            submitExperienceUnauthenticatedSaga,
        ),
        takeLatest(ARTICLE_ADD_EXPERIENCE_SAVE_DRAFT, saveDraftSaga),
        takeLatest(ARTICLE_ADD_EXPERIENCE_LOAD_PHOTOS, loadPhotosSaga),
    ]);
}
